/**
 * Tab Key Constants
 * These constants represent the normalized tab keys used throughout the application
 * They map to the actual Sitecore TabKey values but provide a consistent interface
 */

// Normalized tab keys used in the application for conditions and data management
export const TAB_KEYS = {
  REFERRALS: 'referrals',
  NON_REFERRALS: 'nonReferrals',
  PROCESSED_REFERRALS: 'processedReferrals',
  FLAGGED_RECORDS: 'flaggedRecords',
} as const;

// Sitecore TabKey values mapping to normalized keys
export const SITECORE_TAB_KEY_MAP: Record<string, string> = {
  AvacareLMAReferral: TAB_KEYS.REFERRALS,
  AvacareLMANonReferral: TAB_KEYS.NON_REFERRALS,
  AvacareLMAProcessedToCC: TAB_KEYS.PROCESSED_REFERRALS,
  AvacareLMAFlaggedRecords: TAB_KEYS.FLAGGED_RECORDS,
};

// Helper function to normalize Sitecore tab keys
export const normalizeTabKey = (sitecoreTabKey: string): string => {
  return SITECORE_TAB_KEY_MAP[sitecoreTabKey] || sitecoreTabKey.toLowerCase();
};

// Type for tab key values
export type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

export const FLAG_LEAD_STATUS = {
  REQUESTED: 'requested',
  FLAGGED: 'Flagged',
  PROCESSED: 'processed',
  UNFLAGGED: 'Unflagged',
} as const;

export type LeadStatusValue = (typeof LEAD_STATUS)[keyof typeof LEAD_STATUS];

/**
 * Default text values for flag feature
 * Hardcoded tooltip text and filter defaults
 */
export const FLAG_DEFAULTS = {
  FLAG_TOOLTIP: 'Flag Patient',
  UNFLAG_TOOLTIP: 'Unflag Patient',
  FILTER_LABEL: 'Flagged Records',
  FILTER_KEY: 'isFlagged',
} as const;

/**
 * Quick Filter key constants
 * Used by DynamicFilterPanel, filter apply/remove logic, and useLMA hooks
 */
export const QUICK_FILTER_KEYS = {
  LOCKED_BY_ME: 'lockedByMe',
} as const;

/**
 * When "Locked by me" is on but the user has no locks in Redis/SQL, we still set filters.leadIds to this
 * value so the quick filter stays applied (Redux + UI) and the API can return zero rows (e.g. IN (-1)).
 * Must not match a real LeadId.
 */
export const LOCKED_BY_ME_NO_LOCKS_LEAD_IDS = '-1';

/**
 * Status Constants
 * These constants represent the possible status values for referral records
 */
export const STATUS = {
  MANUAL_REVIEW_REQUIRED: 'Manual-Review-Required',
  AUTO_MERGE: 'Auto-Merge',
  NEW_RECORD: 'New-Record',
  RETURNING_USER: 'Returning-User',
  REGISTERED_USER: 'registered-user',
} as const;

export type Status = (typeof STATUS)[keyof typeof STATUS];

export const LEAD_STATUS = {
  PROCESSED: 'Processed',
  REQUESTED: 'Requested',
} as const;

export type LeadStatus = (typeof LEAD_STATUS)[keyof typeof LEAD_STATUS];

export const RESOURCE_TYPES = [
  { key: 'conditions', label: 'Conditions' },
  { key: 'procedures', label: 'Procedures' },
  { key: 'medications', label: 'Medications' },
  { key: 'immunizations', label: 'Immunizations' },
  { key: 'allergies', label: 'Allergies' },
  { key: 'familyMemberHistory', label: 'Family Member History' },
  { key: 'devices', label: 'Devices' },
] as const;

export type ResourceType = (typeof RESOURCE_TYPES)[number];

/** Max retries for Send to CC, Merge modal, and Sync before showing final error. */
export const MAX_OPERATION_RETRIES = 3;
